'use client';

import { getAuthSessionHintCookieCandidates } from '@/lib/auth/auth-session-hint-cookie';
import { useEffect, useState } from 'react';

export type AuthSessionHintStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated';

export type AuthSessionHintUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  role?: string | null;
  preferredLocale?: string | null;
  [key: string]: unknown;
};

const AUTH_SESSION_HINT_COOKIE_CANDIDATES =
  getAuthSessionHintCookieCandidates();
const AUTH_SESSION_TOKEN_COOKIE_CANDIDATES = [
  '__Secure-better-auth.session_token',
  'better-auth.session_token',
] as const;

function hasAuthSessionHintValue(cookieHeader: string): boolean {
  if (!cookieHeader.trim()) {
    return false;
  }

  return [...AUTH_SESSION_HINT_COOKIE_CANDIDATES].some((cookieName) =>
    cookieHeader.includes(`${cookieName}=1`)
  );
}

function hasAuthSessionTokenValue(cookieHeader: string): boolean {
  if (!cookieHeader.trim()) {
    return false;
  }

  return AUTH_SESSION_TOKEN_COOKIE_CANDIDATES.some((cookieName) =>
    cookieHeader.includes(`${cookieName}=`)
  );
}

function hasAuthSessionPreloadSignal(cookieHeader: string): boolean {
  return (
    hasAuthSessionHintValue(cookieHeader) ||
    hasAuthSessionTokenValue(cookieHeader)
  );
}

interface UseHasAuthSessionHintOptions {
  enabled?: boolean;
}

export type AuthSessionHintState = {
  hasAuthSessionHint: boolean;
  isResolved: boolean;
};

const INITIAL_AUTH_SESSION_HINT_STATE: AuthSessionHintState = {
  hasAuthSessionHint: false,
  isResolved: false,
};

const DISABLED_AUTH_SESSION_HINT_STATE: AuthSessionHintState = {
  hasAuthSessionHint: false,
  isResolved: true,
};

export function useAuthSessionHint(options: UseHasAuthSessionHintOptions = {}) {
  const { enabled = true } = options;
  const [state, setState] = useState<AuthSessionHintState>(
    INITIAL_AUTH_SESSION_HINT_STATE
  );

  useEffect(() => {
    if (!enabled) {
      setState(INITIAL_AUTH_SESSION_HINT_STATE);
      return;
    }

    if (typeof document === 'undefined') {
      return;
    }

    const syncHasAuthSessionHint = () => {
      // Keep the first client render aligned with SSR. We only read browser
      // cookies after mount, and only when the caller explicitly enables it.
      // Keep the cheap hint as the main path, but fall back to the real auth
      // cookie so UI recovery still works when the hint cookie lags behind.
      const hasAuthSessionHint = hasAuthSessionPreloadSignal(document.cookie);
      setState((current) =>
        current.isResolved && current.hasAuthSessionHint === hasAuthSessionHint
          ? current
          : {
              hasAuthSessionHint,
              isResolved: true,
            }
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncHasAuthSessionHint();
      }
    };

    syncHasAuthSessionHint();
    window.addEventListener('focus', syncHasAuthSessionHint);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', syncHasAuthSessionHint);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  return enabled ? state : DISABLED_AUTH_SESSION_HINT_STATE;
}

export function useHasAuthSessionHint(
  options: UseHasAuthSessionHintOptions = {}
) {
  return useAuthSessionHint(options).hasAuthSessionHint;
}
